/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import { isEmpty, isNil, compact } from "lodash"
import dayjs from "dayjs"
import { filterOutlets } from "pages/feedback/FeedbackFilters/utils"
import { AppState } from "store/store.type"
import {
  ListReviewsRequest,
  ReviewReply,
  ReviewReplyReason,
  RequestCsvReportBody,
  FeedbackOverviewResponse,
} from "types/api/menuFeedback.type"
import { MerchantStoreInfo, BusinessType } from "types/models/menu"
import { storageHelper } from "utils/storage"
import { getGrabXValueFromLocalStorage } from "grabx/getter"
import { MerchantPortalFeatureFlag } from "constants/features"
import {
  FEEDBACK_OVERVIEW_REQUEST_DELAY,
  ServiceType,
} from "constants/feedback"
import { HTTPRequestFactory } from "common/utils/network"
import { ReviewEndpoint } from "constants/endpoints"
import { concat, of } from "rxjs"
import { concatMap, delay, toArray } from "rxjs/operators"

export const filterMerchantIDsByBusinessType = ({
  currentMerchantIDFilters,
  outlets,
  selectedBusinessType,
}: {
  currentMerchantIDFilters: string[]
  outlets?: MerchantStoreInfo[]
  selectedBusinessType?: BusinessType
}) => {
  if (isEmpty(outlets) || !Array.isArray(outlets))
    return currentMerchantIDFilters

  let filteredOutletIds = []
  if (selectedBusinessType === BusinessType.Shop) {
    const martOutletIDs = outlets
      .filter(o => o.businessType === BusinessType.Shop)
      .map(mo => mo.merchantID)
    filteredOutletIds = currentMerchantIDFilters.filter((cmid: string) =>
      martOutletIDs.includes(cmid),
    )
  } else {
    const deliveriesOutletIDs = outlets
      .filter(o => !o.businessType)
      .map(mo => mo.merchantID)
    filteredOutletIds = currentMerchantIDFilters.filter((cmid: string) =>
      deliveriesOutletIDs.includes(cmid),
    )
  }

  return filteredOutletIds
}

export function generateFetchReviewsParams(
  body: ListReviewsRequest,
  state: AppState,
): {
  headers: {
    merchantid: string
    requestsource: string
  }
  params: {
    [index: string]:
      | string
      | number
      | undefined
      | null
      | number[]
      | string[]
      | boolean
  }
} {
  const filteredOutlets = filterOutlets({
    outlets: state.menu?.outlets || [],
    cityName: body.cityName,
    businessType: body.businessType,
  })

  const merchantIDs: string[] =
    (!isEmpty(body.merchantIDs)
      ? body.merchantIDs
      : filteredOutlets?.map(o => o.merchantID)) || []

  const firstMerchantID =
    Array.isArray(merchantIDs) && merchantIDs?.length > 0 ? merchantIDs[0] : ""

  const cloneBody: {
    [index: string]: any
  } = { ...body }

  if (!isNil(cloneBody.businessType)) {
    // rename to match BE param
    cloneBody.businessTypeFilter = cloneBody.businessType
    delete cloneBody.businessType
  }

  const enableLargerStoreSupportFeedback = getGrabXValueFromLocalStorage<
    boolean
  >(MerchantPortalFeatureFlag.portalEnableLargerStoreListSupportFeedback)

  return {
    headers: {
      merchantid: firstMerchantID,
      requestsource: "troyPortal",
    },
    params: {
      ...cloneBody,
      merchantIDs: enableLargerStoreSupportFeedback
        ? body.merchantIDs
        : filterMerchantIDsByBusinessType({
            currentMerchantIDFilters: merchantIDs,
            outlets: filteredOutlets,
            selectedBusinessType: body.businessType,
          }),
    },
  }
}

export function generateTempReviewReplies({
  replyID,
  description = "",
}: Partial<ReviewReply>): ReviewReply[] {
  const reviewReplies: ReviewReply[] = [
    {
      replyID,
      description,
      repliedBy: storageHelper.getEmail(),
      createdAt: dayjs().toISOString(),
      updatedAt: dayjs().toISOString(),
      uneditableReason: ReviewReplyReason.ReviewUnreplyableReasonReplyable,
    },
  ]

  return reviewReplies
}

export function generateRequestCsvBody(
  state: AppState,
): RequestCsvReportBody | undefined {
  const { filterState } = state.feedback

  if (!filterState) return undefined

  const filteredOutlets = filterOutlets({
    outlets: state.menu?.outlets || [],
    cityName: filterState.cityName,
    businessType: filterState.businessType,
  })

  return {
    startDate: filterState.startDate,
    endDate: filterState.endDate,
    queryNames: ["customer-reviews-download"],
    extraFilters: {
      cityName: filterState.cityName,
      serviceType: filterState.serviceType,
      businessTypeFilter: filterState.businessType,
      merchantIDs: !isEmpty(filterState.merchantIDs)
        ? filterState.merchantIDs
        : filteredOutlets?.map(outlet => outlet.merchantID) || [],
      reviewStatus: filterState.status,
    },
  }
}

// helper function to create requests body for each chunk
export const createRequestsForChunks = (
  request: any,
  serviceType: ServiceType,
  merchantIDChunks: string[][] = [],
): any[] =>
  merchantIDChunks.map(merchantIDChunk => ({
    ...request,
    params: {
      ...request.params,
      serviceType,
      merchantIDs: merchantIDChunk,
    },
  }))

// helper function to create and execute requests for each batch of feedback overviews
export const fetchFeedbackOverviewsBatch = (
  httpRequest: HTTPRequestFactory,
  requests: any[],
) => {
  return concat(
    ...requests.map((req, index: number) =>
      of(req).pipe(
        delay(index * FEEDBACK_OVERVIEW_REQUEST_DELAY), // add delay between request
        // eslint-disable-next-line @typescript-eslint/no-shadow
        concatMap(req =>
          httpRequest.GET<FeedbackOverviewResponse>({
            ...req,
            endpoint: ReviewEndpoint.getReviewOverviewsV1,
          }),
        ),
      ),
    ),
  ).pipe(toArray())
}

// function to aggregate feedback overview responses, useful when processing responses in batches
export const aggregateFeedbackResponses = (responses: any[]): any => {
  if (!responses || compact(responses).length === 0) {
    return null
  }

  const aggregatedResult = {
    aggregatedRatingScore: 0,
    ratingCount: 0,
    ratingDistribution: [
      { score: 1, countPercentage: 0, actualCount: 0 },
      { score: 2, countPercentage: 0, actualCount: 0 },
      { score: 3, countPercentage: 0, actualCount: 0 },
      { score: 4, countPercentage: 0, actualCount: 0 },
      { score: 5, countPercentage: 0, actualCount: 0 },
    ],
  } as any

  let totalWeightedScore = 0

  responses.forEach(response => {
    const feedbackOverview = response?.data?.feedbackOverview

    if (!feedbackOverview) {
      return // skip this response if data is missing
    }

    // update total rating count
    aggregatedResult.ratingCount += feedbackOverview.ratingCount

    // calculate and accumulate the weighted score
    totalWeightedScore +=
      feedbackOverview.aggregatedRatingScore * feedbackOverview.ratingCount

    // accumulate actual counts for each score
    feedbackOverview.ratingDistribution.forEach(
      (distribution: any, index: number) => {
        const actualCount =
          (distribution.countPercentage / 100) * feedbackOverview.ratingCount
        aggregatedResult.ratingDistribution[index].actualCount += actualCount
      },
    )
  })

  // calculate the weighted average for aggregatedRatingScore
  if (aggregatedResult.ratingCount > 0) {
    aggregatedResult.aggregatedRatingScore = parseFloat(
      (totalWeightedScore / aggregatedResult.ratingCount).toFixed(1),
    )
  } else {
    aggregatedResult.aggregatedRatingScore = 0 // Default to 0 if no ratings to avoid NaN result
  }

  // recalculate percentages
  aggregatedResult.ratingDistribution = aggregatedResult.ratingDistribution.map(
    (distribution: any) => {
      return {
        score: distribution.score,
        countPercentage: Math.round(
          (distribution.actualCount / aggregatedResult.ratingCount) * 100,
        ),
      }
    },
  )

  return aggregatedResult
}
